import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { GameBoard } from "@/components/game/GameBoardV2";
import { WinModal } from "@/components/game/WinModalV2";
import { LoseModal } from "@/components/game/LoseModalV2";
import { HowToPlayModal } from "@/components/game/HowToPlayModalV2";
import { 
  DoublePointsAlert, 
  DoublePointsIndicator, 
  DoubleXPIndicator,
  InstaEliminationPicker, 
  InstaEliminationIndicator,
  ShuffleAlert
} from "@/components/game/PowerUpAlerts";
import { InventoryPanel } from "@/components/game/InventoryPanel";
import { NewFruitUnlockedModal, useNewFruitNotification } from "@/components/game/NewFruitUnlocked";
import { SettingsModal } from "@/components/game/SettingsModal";
import { ExitLevelWarning } from "@/components/game/ExitLevelWarning";
import { MissionProgress } from "@/components/game/MissionProgress";
import { useAdvancedHint, HintTooltip } from "@/components/game/AdvancedHintSystem";
import { BossHPBar, DragonVisual, OwlGuardianVisual, KrakenVisual, PhoenixVisual, DreamWeaverVisual, AlchemistVisual, RingmasterVisual, PirateCaptainVisual, TempleGuardianVisual, StormKingVisual, FungalKingVisual, CasinoDealerVisual, CosmicEntityVisual, PharaohVisual, FrostGiantVisual, AttackWarning, BossVictoryModal, useBossFight } from "@/components/game/BossFight";
import { GameFloatingMenu, GameHintButton, GameHeader } from "@/components/game/GameFloatingMenu";
import { SwordSelectionModal } from "@/components/game/SwordSelectionModal";
import { VIPItemsBar, VIPItemUseModal, VIPEffectIndicator, useVIPItems, VIP_ITEMS_CONFIG } from "@/components/game/VIPItemsPanel";
import { useGameState } from "@/hooks/useGameStateV2";
import { useSoundEffects } from "@/hooks/useSoundEffects";
import { useMusicSettings } from "@/hooks/useMusicSettings";
import { useHealth } from "@/hooks/useHealth";
import { useXP, getXPForNextLevel } from "@/hooks/useXP";
import { useHaptic } from "@/hooks/useHaptic";
import { useChallenges } from "@/hooks/useChallenges";
import { useDailyChallenges } from "@/hooks/useDailyChallenges";
import { useAuth, updateLeaderboard } from "@/hooks/useAuth";
import { useShop, CONSUMABLES, getItemById } from "@/hooks/useShop";
import { useCosmetics } from "@/hooks/useCosmetics";
import { getLevelById, markLevelCompleted, saveLevelScore, getLevelStars, LEVELS, getMissionDescription, getCompletedLevels } from "@/config/levels";
import { Button } from "@/components/ui/button";
import { Swords } from "lucide-react";
import { toast } from "sonner";

// Sword bonus multipliers (points per move used)
const SWORD_BONUS_MULTIPLIERS = {
  'bronze_sword': 3,
  'silver_sword': 5,
  'golden_sword': 10,
  'diamond_sword': 15,
  'master_sword': 25,
};

const SWORD_INFO = {
  'bronze_sword': { name: 'Bronze', emoji: '🥉' },
  'silver_sword': { name: 'Silver', emoji: '🥈' },
  'golden_sword': { name: 'Golden', emoji: '🥇' },
  'diamond_sword': { name: 'Diamond', emoji: '💎' },
  'master_sword': { name: 'Master', emoji: '👑' },
};

export default function GamePage() {
  const { levelId } = useParams();
  const location = useLocation();
  const pvpData = location.state?.pvp || null;
  const currentLevelId = parseInt(levelId, 10) || 1;
  const levelConfig = getLevelById(currentLevelId);
  
  // API URL for fetching data
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Membership status for premium benefits
  const [membershipStatus, setMembershipStatus] = useState(null);
  
  // Get highest completed game level for sword tier calculation
  const completedLevels = getCompletedLevels();
  const highestCompletedLevel = completedLevels.length > 0 ? Math.max(...completedLevels) : 0;
  
  // Check if this is a boss level
  const isBossLevel = levelConfig?.missionType === 'boss_fight';
  const bossConfig = levelConfig?.boss;
  
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showExitWarning, setShowExitWarning] = useState(false);
  const [showBossVictory, setShowBossVictory] = useState(false);
  const [showSwordSelection, setShowSwordSelection] = useState(false);
  const [showDragonScaleHit, setShowDragonScaleHit] = useState(false); // Fire burst effect when Dragon Scale damage is applied
  const [pvpResult, setPvpResult] = useState(null); // PvP match result
  
  // Use global music settings for sound effects
  const { soundEffectsEnabled, fadeOutMusic, fadeInMusic, isPlaying: isMusicPlaying } = useMusicSettings();
  
  // Fade out music when game page loads
  useEffect(() => {
    if (isMusicPlaying) {
      fadeOutMusic(1500); // 1.5 second fade out
    }
    
    // Fade music back in when leaving the game page
    return () => {
      // Music will be restarted by the MusicProvider when returning to level map
    };
  }, []); // Only run on mount
  
  // XP state for win modal animation
  const [xpAnimationData, setXpAnimationData] = useState(null);
  
  // Item drops from level completion
  const [itemDrops, setItemDrops] = useState([]);
  
  // Ghost score - friend's best on this level
  const [ghostScore, setGhostScore] = useState(null);
  
  // Fetch friend ghost score for this level
  useEffect(() => {
    const fetchGhostScore = async () => {
      const token = localStorage.getItem('fruitCrush_token');
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/friends/level-score/${currentLevelId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.best_friend) setGhostScore(data.best_friend);
        }
      } catch (e) { /* silent */ }
    };
    fetchGhostScore();
  }, [currentLevelId, API_URL]);
  
  // Restart confirmation dialog
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  
  // Fetch membership status for premium benefits
  useEffect(() => {
    const fetchMembershipStatus = async () => {
      const token = localStorage.getItem('fruitCrush_token');
      if (!token) return;
      
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.user_id;
        
        const response = await fetch(`${API_URL}/api/stripe/membership-status/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setMembershipStatus(data);
        }
      } catch (error) {
        console.error('Failed to fetch membership status:', error);
      }
    };
    fetchMembershipStatus();
  }, [API_URL]);
  
  // New fruit notification
  const { showModal: showNewFruitModal, unlockedFruit, checkAndShowUnlock, closeModal: closeNewFruitModal } = useNewFruitNotification();
  
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { hp, maxHP, hpPercentage, takeDamage, healHP, canPlay } = useHealth();
  const { totalXP, level, currentXP, neededXP, prestige, addXP, checkLevelUp } = useXP();
  const { addEliminatedFruits, addDiscoCombos, addStripedFruits, addWrappedFruits, addSpecialCombos, addLevelCompleted, calculateMasteryBonus } = useChallenges();
  const { updateProgress: updateDailyProgress } = useDailyChallenges();
  const { 
    awardStars, 
    awardCoins,
    calculateCoins,
    inventory, 
    equipment,
    equippedItems,
    consumeItem, 
    getConsumableCount,
    getActiveEffects,
    earnedStarsLevels,
    addBattlePassXP,
    battlePass,
    addStars,
    getEquipmentStatus,
    consumeEquipmentUse,
    rechargeEquipment
  } = useShop();
  
  // Cosmetics
  const { equippedSkin, equippedEffect, equippedTheme } = useCosmetics();
  
  // VIP Items system
  const userId = user?.id;
  const vipItems = useVIPItems(userId);
  const [vipItemPending, setVipItemPending] = useState(null); // Item waiting for board/obstacle selection
  const [vipItemModal, setVipItemModal] = useState(null); // Item to show in modal
  const [vipCoinBonus, setVipCoinBonus] = useState(0); // Bonus coins from Topaz Token
  
  // Fetch VIP inventory on mount
  useEffect(() => {
    if (userId) {
      vipItems.fetchInventory();
    }
  }, [userId]);
  
  // Track if stars were already earned for this level
  const alreadyEarnedStars = earnedStarsLevels[currentLevelId.toString()] || 0;
  
  // Check if God Sword is equipped
  const hasGodSword = equippedItems?.weapon === 'god_sword';
  
  // State for showing inventory panel
  const [showInventory, setShowInventory] = useState(false);
  
  // Get equipped sword from localStorage
  const equippedSword = localStorage.getItem('fruitCrush_equippedSword');
  const swordMultiplier = equippedSword ? SWORD_BONUS_MULTIPLIERS[equippedSword] || 0 : 0;
  const swordInfo = equippedSword ? SWORD_INFO[equippedSword] : null;
  
  const {
    board,
    selectedCell,
    score,
    movesLeft,
    fruitsCollected,
    dualFruitsCollected,
    gameStatus,
    handleCellClick,
    handleSwipe,
    resetGame,
    isAnimating,
    explodingCells,
    clearEffect,
    boardSize,
    swappingCells,
    lastEliminatedFruits,
    lastDiscoCombo,
    lastStripedCreated,
    lastWrappedCreated,
    lastSpecialComboTriggered,
    lastMatchInfo, // For boss damage calculation
    // Combo system
    doublePointsActive,
    showDoublePointsAlert,
    // Insta-elimination
    instaEliminationActive,
    showInstaEliminationPicker,
    useInstaElimination,
    cancelInstaElimination,
    openInstaEliminationPicker,
    // Shuffle
    showShuffleAlert,
    // Sword ability
    swordMode,
    swordAbilityUsed,
    activateSwordMode,
    cancelSwordMode,
    useSwordAbility,
    swordTier,
    swordSlashAnimation,
    // Special combo animation
    specialComboAnimation,
    // Consumable functions
    addBonusMoves,
    triggerShuffle,
    bombMode,
    activateBombMode,
    cancelBombMode,
    executeBomb,
    colorBombMode,
    activateColorBombMode,
    cancelColorBombMode,
    executeColorBomb,
    hintCell,
    showHint,
    // 2X XP consumable
    doubleXPActive,
    doubleXPTurnsLeft,
    activateDoubleXP,
    // Wrapped fruit placement
    wrappedMode,
    activateWrappedMode,
    cancelWrappedMode,
    executeWrappedPlacement,
    // Disco fruit placement
    discoMode,
    activateDiscoMode,
    cancelDiscoMode,
    executeDiscoPlacement,
    // Striped fruit placement
    stripedMode,
    activateStripedMode,
    cancelStripedMode,
    executeStripedPlacement,
    // Mission system
    missionType,
    missionProgress,
    // Enhanced animations
    showFruityCrush,
    screenShake,
    cascadeCount,
    showComboMultiplier,
    showLevelIntro,
    setShowLevelIntro,
    showUrgency,
    // Combo name system
    comboName,
    showComboName,
    // Sword uses
    swordUsesRemaining,
    maxSwordUses,
    // God Sword
    godSwordMode,
    godSwordUsesRemaining,
    godSwordAnimation,
    activateGodSwordMode,
    cancelGodSwordMode,
    activateGodSword,
    initializeGodSwordUses,
    // Milestone Item Abilities
    crystalBallActive,
    crystalBallPreview,
    activateCrystalBall,
    moonStoneActive,
    activateMoonStone,
    thunderStrikeAnimation,
    activateThunderStrike,
    dragonScaleActive,
    activateDragonScale,
    oceanPearlActive,
    activateOceanPearl,
    tridentShardAnimation,
    activateTridentShard,
    frostCrystalAnimation,
    activateFrostCrystal,
    auroraEssenceActive,
    activateAuroraEssence,
    // New item abilities
    activateFruitMagnet,
    activateTsunamiWave,
    guardianShieldActive,
    activateGuardianShield,
    deactivateGuardianShield,
    activateEyeOfRa,
    yetiFurTurns,
    activateYetiFur,
    snowGlobeTurns,
    activateSnowGlobe,
    icePickMode,
    activateIcePick,
    activatePharaohScarab,
    ankhOfLifeActive,
    activateAnkhOfLife,
    // VIP Item abilities
    addMoves,
    triggerVIPShuffle,
    spawnRandomSpecials,
    clearObstacleType,
    executeCrossBlast,
    // World Mechanics
    worldMechanic,
    lavaLevel,
    movesSinceLavaRise,
    lavaRiseRate,
    isDayTime,
    movesSinceCycleChange,
    dayNightTransition,
    cycleLength,
    gravityDirection,
    gravityShiftPending,
    lightningCells,
    showLightning,
    activeMultiplier,
    showJackpot,
    scorePopups,
    setScorePopups,
  } = useGameState(levelConfig, highestCompletedLevel, getActiveEffects(), calculateMasteryBonus, pvpData?.boardSeed || null);

  // Boss fight hook - now with Guardian Shield support and level-scaled HP
  const bossFight = useBossFight(bossConfig, guardianShieldActive, deactivateGuardianShield, currentLevelId);

  // Calculate moves used and sword bonus (after movesLeft is available)
  const movesUsed = levelConfig ? levelConfig.moves - movesLeft : 0;
  const swordBonus = swordMultiplier * movesUsed;

  const { playSound } = useSoundEffects(soundEffectsEnabled);
  const haptic = useHaptic();
  
  // God Sword initialization and handling - MUST be after useGameState
  useEffect(() => {
    if (hasGodSword && initializeGodSwordUses && getEquipmentStatus) {
      const fetchGodSwordStatus = async () => {
        const status = await getEquipmentStatus('god_sword');
        if (status) {
          initializeGodSwordUses(status.uses_remaining);
        }
      };
      fetchGodSwordStatus();
    }
  }, [hasGodSword, initializeGodSwordUses, getEquipmentStatus]);
  
  const handleGodSwordUse = useCallback(async () => {
    if (godSwordUsesRemaining > 0 && activateGodSword && consumeEquipmentUse) {
      activateGodSword();
      await consumeEquipmentUse('god_sword');
    }
  }, [godSwordUsesRemaining, activateGodSword, consumeEquipmentUse]);
  
  // Advanced hint system
  const {
    hintCells: advancedHintCells,
    hintMessage,
    hintType,
    showHint: showAdvancedHint,
    clearHint,
    autoHintEnabled,
    toggleAutoHint,
  } = useAdvancedHint(board, levelConfig?.targetFruit, boardSize);
  
  // Count total inventory items
  const totalInventoryItems = CONSUMABLES.reduce((sum, item) => sum + (inventory[item.id] || 0), 0);
  
  // Handle using a consumable
  const handleUseConsumable = useCallback((itemId) => {
    const item = getItemById(itemId);
    if (!item) return false;
    
    // Check if we have this item
    if (!inventory[itemId] || inventory[itemId] <= 0) return false;
    
    let success = false;
    
    // First check by effect type, then fall back to itemId
    const effectType = item.effect?.type;
    
    switch (effectType) {
      case 'heal':
        healHP(item.effect.value);
        success = true;
        break;
      case 'striped':
        success = activateStripedMode();
        break;
      case 'moves':
        success = addBonusMoves(item.effect.value);
        break;
      case 'doubleXP':
        success = activateDoubleXP();
        break;
      case 'bomb':
        success = activateBombMode();
        break;
      case 'colorBomb':
        success = activateColorBombMode();
        break;
      case 'shuffle':
        success = triggerShuffle();
        break;
      case 'hint':
        success = showHint();
        break;
      case 'wrapped':
        success = activateWrappedMode();
        break;
      case 'disco':
        success = activateDiscoMode();
        break;
      // === MILESTONE ITEM EFFECT TYPES ===
      case 'preview': // crystal_ball
        success = activateCrystalBall();
        break;
      case 'highlight': // moon_stone
        success = activateMoonStone();
        break;
      case 'clearColumnRow': // thunder_strike
        success = activateThunderStrike();
        break;
      case 'bossDamage': // dragon_scale
        success = activateDragonScale();
        break;
      case 'magnet': // fruit_magnet
        success = activateFruitMagnet();
        break;
      case 'clearRows': // tsunami_wave
        success = activateTsunamiWave();
        break;
      case 'blockBoss': // guardian_shield
        success = activateGuardianShield();
        break;
      // === OCEAN DEPTHS ITEMS ===
      case 'whirlpool': // ocean_pearl
        success = activateOceanPearl();
        break;
      case 'strikeRandom': // trident_shard
        success = activateTridentShard();
        break;
      // === FROZEN PEAKS ITEMS ===
      case 'freezeImmune': // yeti_fur
        success = activateYetiFur();
        break;
      case 'freezeObstacles': // snow_globe
        success = activateSnowGlobe();
        break;
      case 'breakFrozen': // ice_pick
        success = activateIcePick();
        break;
      // === ANCIENT RUINS ITEMS ===
      case 'removeCurse': // pharaoh_scarab
        success = activatePharaohScarab();
        break;
      case 'extraLife': // ankh_of_life
        success = activateAnkhOfLife();
        break;
      case 'revealMatches': // eye_of_ra
        success = activateEyeOfRa();
        break;
      default:
        // Fallback: try matching by itemId directly
        switch (itemId) {
          case 'crystal_ball':
            success = activateCrystalBall();
            break;
          case 'moon_stone':
            success = activateMoonStone();
            break;
          case 'thunder_strike':
            success = activateThunderStrike();
            break;
          case 'dragon_scale':
            success = activateDragonScale();
            break;
          case 'ocean_pearl':
            success = activateOceanPearl();
            break;
          case 'trident_shard':
            success = activateTridentShard();
            break;
          case 'frost_crystal':
            success = activateFrostCrystal();
            break;
          case 'aurora_essence':
            success = activateAuroraEssence();
            break;
          case 'fruit_magnet':
            success = activateFruitMagnet();
            break;
          case 'tsunami_wave':
            success = activateTsunamiWave();
            break;
          case 'guardian_shield':
            success = activateGuardianShield();
            break;
          case 'eye_of_ra':
            success = activateEyeOfRa();
            break;
          case 'yeti_fur':
            success = activateYetiFur();
            break;
          case 'snow_globe':
            success = activateSnowGlobe();
            break;
          case 'ice_pick':
            success = activateIcePick();
            break;
          case 'pharaoh_scarab':
            success = activatePharaohScarab();
            break;
          case 'ankh_of_life':
            success = activateAnkhOfLife();
            break;
          default:
            return false;
        }
    }
    
    if (success) {
      consumeItem(itemId);
      playSound('select');
      haptic.tap();
    }
    
    return success;
  }, [inventory, healHP, activateStripedMode, addBonusMoves, activateDoubleXP, activateBombMode, activateColorBombMode, triggerShuffle, showHint, activateWrappedMode, activateDiscoMode, consumeItem, playSound, haptic, activateCrystalBall, activateMoonStone, activateThunderStrike, activateDragonScale, activateOceanPearl, activateTridentShard, activateFrostCrystal, activateAuroraEssence, activateFruitMagnet, activateTsunamiWave, activateGuardianShield, activateEyeOfRa, activateYetiFur, activateSnowGlobe, activateIcePick, activatePharaohScarab, activateAnkhOfLife]);

  // Track eliminated fruits for challenges
  useEffect(() => {
    if (lastEliminatedFruits && Object.keys(lastEliminatedFruits).length > 0) {
      addEliminatedFruits(lastEliminatedFruits);
      // Track total fruits for daily challenges
      const totalFruits = Object.values(lastEliminatedFruits).reduce((sum, count) => sum + count, 0);
      updateDailyProgress('total_fruits', totalFruits);
      // Track individual fruit types
      Object.entries(lastEliminatedFruits).forEach(([fruit, count]) => {
        if (count > 0) {
          updateDailyProgress(fruit, count);
        }
      });
    }
  }, [lastEliminatedFruits, addEliminatedFruits, updateDailyProgress]);
  
  // Track disco combos for challenges
  useEffect(() => {
    if (lastDiscoCombo) {
      addDiscoCombos(1);
      updateDailyProgress('disco_combos', 1);
      playSound('discoClear');
    }
  }, [lastDiscoCombo, addDiscoCombos, updateDailyProgress, playSound]);

  // Track striped fruits created for challenges
  useEffect(() => {
    if (lastStripedCreated > 0) {
      addStripedFruits(lastStripedCreated);
      updateDailyProgress('striped_fruits', lastStripedCreated);
    }
  }, [lastStripedCreated, addStripedFruits, updateDailyProgress]);

  // Track wrapped fruits created for challenges
  useEffect(() => {
    if (lastWrappedCreated > 0) {
      addWrappedFruits(lastWrappedCreated);
      updateDailyProgress('wrapped_fruits', lastWrappedCreated);
    }
  }, [lastWrappedCreated, addWrappedFruits, updateDailyProgress]);

  // Track special combos (Striped+Striped, Striped+Wrapped) for challenges
  const isFirstSpecialComboRender = React.useRef(true);
  useEffect(() => {
    if (isFirstSpecialComboRender.current) {
      isFirstSpecialComboRender.current = false;
      return;
    }
    addSpecialCombos(1);
    updateDailyProgress('special_combos', 1);
  }, [lastSpecialComboTriggered, addSpecialCombos, updateDailyProgress]);

  // Boss fight: Calculate and deal damage when fruits are matched
  const lastProcessedFruitsRef = useRef(null);
  
  // Reset damage tracking when level changes
  useEffect(() => {
    lastProcessedFruitsRef.current = null;
  }, [levelId]);
  
  useEffect(() => {
    if (!isBossLevel || !lastEliminatedFruits || bossFight.isDefeated) {
      return;
    }
    
    const fruitsMatched = Object.keys(lastEliminatedFruits);
    if (fruitsMatched.length === 0) return;
    
    // Create a simple hash to check if this is truly new data
    const fruitsHash = JSON.stringify(lastEliminatedFruits);
    if (fruitsHash === lastProcessedFruitsRef.current) {
      return;
    }
    lastProcessedFruitsRef.current = fruitsHash;
    
    // Count total fruits eliminated
    const totalFruits = Object.values(lastEliminatedFruits).reduce((sum, count) => sum + count, 0);
    
    // Damage calculation (BUFFED for faster boss fights):
    // - Base: 25 damage per fruit type (was 10)
    // - Bonus: 5 damage per actual fruit count (was 2, capped at 30 fruits)
    // - God Sword bonus: If 50+ fruits cleared, add 200 bonus damage (was 100)
    // - Dragon Scale bonus: +50% damage when active
    // - Golden Dragon Scale (VIP): +500% damage (6x multiplier)
    const baseDamage = fruitsMatched.length * 25;
    const countBonus = Math.min(totalFruits, 30) * 5;
    const godSwordBonus = totalFruits >= 50 ? 200 : 0;
    
    let damage = baseDamage + countBonus + godSwordBonus;
    
    // Apply Dragon Scale bonus (+50% damage)
    if (dragonScaleActive) {
      damage = Math.floor(damage * 1.5);
      // Trigger fire burst visual effect
      setShowDragonScaleHit(true);
      setTimeout(() => setShowDragonScaleHit(false), 800);
    }
    
    // Apply Golden Dragon Scale VIP bonus (+500% damage = 6x multiplier)
    const bossDamageMultiplier = vipItems.getBossDamageMultiplier();
    if (bossDamageMultiplier > 1) {
      damage = Math.floor(damage * bossDamageMultiplier);
      console.log('[Boss] Golden Dragon Scale active! Damage boosted to', damage);
    }
    
    // Cap single trigger at 500 damage (or 750 with Dragon Scale, or 3000 with Golden Dragon Scale)
    const maxDamage = bossDamageMultiplier > 1 ? 3000 : (dragonScaleActive ? 750 : 500);
    damage = Math.min(damage, maxDamage);
    
    
    if (damage > 0) {
      bossFight.dealDamage(damage);
    }
  }, [lastEliminatedFruits, isBossLevel, bossFight, levelId, dragonScaleActive, vipItems]);
  
  // Boss fight: Track moves for dragon attacks
  const prevMovesRef = useRef(movesLeft);
  useEffect(() => {
    if (!isBossLevel || bossFight.isDefeated) return;
    
    // Detect when a move was made (movesLeft decremented)
    if (prevMovesRef.current > movesLeft) {
      bossFight.onMove();
    }
    prevMovesRef.current = movesLeft;
  }, [movesLeft, isBossLevel, bossFight]);
  
  // VIP Items: Track moves for turn-based effects (Golden Crown score boost)
  const prevMovesVIPRef = useRef(movesLeft);
  useEffect(() => {
    // Detect when a move was made
    if (prevMovesVIPRef.current > movesLeft && gameStatus === 'playing') {
      vipItems.decrementTurnEffects();
    }
    prevMovesVIPRef.current = movesLeft;
  }, [movesLeft, gameStatus]);
  
  // VIP Items: Timer for time-based effects (Amethyst Amulet highlight)
  useEffect(() => {
    if (!vipItems.activeEffects.match_highlight) return;
    
    const interval = setInterval(() => {
      vipItems.tickTimeEffects();
    }, 1000);
    
    return () => clearInterval(interval);
  }, [vipItems.activeEffects.match_highlight]);
  
  // Boss fight: Check for victory
  useEffect(() => {
    if (isBossLevel && bossFight.isDefeated && !showBossVictory) {
      // Play victory sound
      playSound('levelWin');
      // Show victory modal
      setTimeout(() => setShowBossVictory(true), 1000);
    }
  }, [bossFight.isDefeated, isBossLevel, showBossVictory, playSound]);

  // Play explosion sound when cells explode
  useEffect(() => {
    if (explodingCells && explodingCells.size > 0) {
      if (explodingCells.size >= 8) {
        // Big explosion for large matches
        playSound('cascade');
      } else {
        playSound('explosion');
      }
    }
  }, [explodingCells, playSound]);

  // Play line clear sound
  useEffect(() => {
    if (clearEffect) {
      if (clearEffect.type === 'row') {
        playSound('lineClear');
      } else if (clearEffect.type === 'column') {
        playSound('columnClear');
      }
    }
  }, [clearEffect, playSound]);

  // Play sword slash sound and track for daily challenges
  useEffect(() => {
    if (swordSlashAnimation) {
      playSound('swordSlash');
      // Track sword usage for daily challenges
      updateDailyProgress('sword_used', 1);
    }
  }, [swordSlashAnimation, playSound, updateDailyProgress]);
  
  // Swipe handler with sound and haptic
  // VIP Item use handler
  const handleUseVIPItem = useCallback(async (item) => {
    const config = VIP_ITEMS_CONFIG[item.id] || item;
    
    // For items that need board selection, show modal first
    if (config.useType === 'board_select') {
      setVipItemModal(item);
      return;
    }
    
    // For obstacle selection items, gather obstacle info first
    if (config.useType === 'obstacle_select') {
      // Get unique obstacle types on board
      const obstacleTypes = [];
      const obstacleCount = {};
      board.forEach(row => {
        row.forEach(cell => {
          if (cell && cell.obstacle && cell.obstacle !== 'none') {
            if (!obstacleCount[cell.obstacle]) {
              obstacleCount[cell.obstacle] = 0;
            }
            obstacleCount[cell.obstacle]++;
          }
        });
      });
      
      Object.entries(obstacleCount).forEach(([type, count]) => {
        obstacleTypes.push({ type, name: type.replace(/_/g, ' '), count });
      });
      
      if (obstacleTypes.length === 0) {
        toast.error('No obstacles on board to clear!');
        return;
      }
      
      setVipItemModal({ ...item, obstacleTypes });
      return;
    }
    
    // Check boss_damage_boost restriction BEFORE consuming item
    if (config.effect === 'boss_damage_boost' && !isBossLevel) {
      toast.error('🐉 Golden Dragon Scale can only be used in boss fights!');
      return;
    }
    
    // Use item from backend
    const result = await vipItems.useItem(item.id);
    if (!result) return;
    
    // Apply instant effects
    switch (config.effect) {
      case 'score_boost':
        vipItems.activateEffect('score_boost', config.duration || 3);
        toast.success(`🎗️ ${config.name} activated! +100% score for ${config.duration} turns!`);
        break;
        
      case 'full_heal':
        healHP(9999); // Heal to max
        toast.success(`💎 ${config.name} used! HP fully restored!`);
        break;
        
      case 'extra_moves':
        addMoves(config.moveBonus || 5);
        toast.success(`💍 ${config.name} used! +${config.moveBonus || 5} moves!`);
        break;
        
      case 'optimal_shuffle':
        triggerVIPShuffle();
        toast.success(`🔮 ${config.name} used! Board shuffled optimally!`);
        break;
        
      case 'double_coins':
        vipItems.activateEffect('double_coins', true);
        toast.success(`🏆 ${config.name} activated! 2x coins for this level!`);
        break;
        
      case 'match_highlight':
        vipItems.activateEffect('match_highlight', config.duration || 30);
        toast.success(`📿 ${config.name} activated! Matches highlighted for ${config.duration}s!`);
        break;
        
      case 'bonus_coins':
        setVipCoinBonus(prev => prev + (config.coinBonus || 500));
        toast.success(`🪙 ${config.name} used! +${config.coinBonus || 500} coins!`);
        break;
        
      case 'spawn_specials':
        spawnRandomSpecials(config.specialCount || 3);
        toast.success(`🌙 ${config.name} used! ${config.specialCount || 3} special fruits spawned!`);
        break;
        
      case 'boss_damage_boost':
        // Boss level check already done above
        vipItems.activateEffect('boss_damage_boost', true);
        toast.success(`🐉 ${config.name} activated! +500% damage to bosses!`);
        break;
        
      default:
        toast.success(`${config.emoji} ${config.name} used!`);
    }
    
    playSound('powerUp');
    haptic.tap();
  }, [board, vipItems, healHP, addMoves, triggerVIPShuffle, spawnRandomSpecials, playSound, haptic, isBossLevel]);
  
  // VIP Item: Confirm modal action (for board/obstacle select)
  const handleVIPItemConfirm = useCallback(async (selectedData) => {
    if (!vipItemModal) return;
    
    const config = VIP_ITEMS_CONFIG[vipItemModal.id] || vipItemModal;
    
    // Use item from backend
    const result = await vipItems.useItem(vipItemModal.id);
    if (!result) {
      setVipItemModal(null);
      return;
    }
    
    if (config.useType === 'board_select') {
      // Royal Scepter - needs cell selection on board
      setVipItemPending(vipItemModal);
      toast.info(`👑 Tap any cell on the board to activate ${config.name}!`);
    } else if (config.useType === 'obstacle_select' && selectedData) {
      // Pearl Pendant - clear all obstacles of selected type
      clearObstacleType(selectedData);
      toast.success(`📿 ${config.name} cleared all ${selectedData.replace(/_/g, ' ')}!`);
      playSound('explosion');
    }
    
    setVipItemModal(null);
  }, [vipItemModal, vipItems, clearObstacleType, playSound]);
  
  // VIP Item: Board cell selected for pending item
  const handleVIPBoardSelect = useCallback((row, col) => {
    if (!vipItemPending) return false;
    
    const config = VIP_ITEMS_CONFIG[vipItemPending.id];
    
    if (config.effect === 'cross_clear') {
      // Royal Scepter - clear row and column
      executeCrossBlast(row, col);
      toast.success(`👑 Royal Scepter activated! Cross blast!`);
      playSound('cascade');
      haptic.impact();
    }
    
    setVipItemPending(null);
    return true;
  }, [vipItemPending, executeCrossBlast, playSound, haptic]);

  const handleSwipeWithSound = useCallback((fromRow, fromCol, toRow, toCol, direction) => {
    playSound('select');
    haptic.tap();
    handleSwipe(fromRow, fromCol, toRow, toCol, direction);
  }, [handleSwipe, playSound, haptic]);
  
  // Click handler with sound and haptic - also handles bomb/color bomb/wrapped/disco modes
  const handleCellClickWithSound = useCallback((row, col) => {
    playSound('select');
    haptic.tap();
    
    // Handle VIP item pending (Royal Scepter cross blast)
    if (vipItemPending) {
      handleVIPBoardSelect(row, col);
      return;
    }
    
    // Handle bomb mode
    if (bombMode) {
      executeBomb(row, col);
      return;
    }
    
    // Handle color bomb mode
    if (colorBombMode) {
      executeColorBomb(row, col);
      return;
    }
    
    // Handle wrapped fruit placement mode
    if (wrappedMode) {
      executeWrappedPlacement(row, col);
      return;
    }
    
    // Handle disco fruit placement mode
    if (discoMode) {
      executeDiscoPlacement(row, col);
      return;
    }
    
    // Handle striped fruit placement mode
    if (stripedMode) {
      executeStripedPlacement(row, col);
      return;
    }
    
    handleCellClick(row, col);
  }, [handleCellClick, playSound, haptic, bombMode, executeBomb, colorBombMode, executeColorBomb, wrappedMode, executeWrappedPlacement, discoMode, executeDiscoPlacement, stripedMode, executeStripedPlacement, vipItemPending, handleVIPBoardSelect]);

  // Check if player can play
  useEffect(() => {
    if (!canPlay) {
      navigate('/');
    }
  }, [canPlay, navigate]);

  // Handle game end - use ref to avoid setState in effect
  const hasProcessedLossRef = useRef(false);
  const hasProcessedWinRef = useRef(false);
  
  // Reset refs when level changes or game resets
  useEffect(() => {
    hasProcessedLossRef.current = false;
    hasProcessedWinRef.current = false;
    setXpAnimationData(null);
  }, [currentLevelId]);
  
  useEffect(() => {
    if (gameStatus === 'won' && !hasProcessedWinRef.current) {
      playSound('win');
      haptic.win();
      hasProcessedWinRef.current = true;
      
      // Mark level as completed
      markLevelCompleted(currentLevelId);
      
      // Track level completion for challenges
      addLevelCompleted(movesLeft >= 10); // Perfect if 10+ moves left
      updateDailyProgress('levels_completed', 1);
      if (movesLeft >= 10) {
        updateDailyProgress('perfect_wins', 1);
      }
      
      // Calculate final score with bonus (XP = total score)
      const bonusPoints = movesLeft * 50;
      const totalScore = score + bonusPoints + swordBonus;
      
      // VIP item bonuses
      const vipScoreMultiplier = vipItems.getScoreMultiplier(); // Golden Crown: 2x score
      const vipDoubleCoinActive = vipItems.hasDoubleCoinBonus(); // Emerald Chalice: 2x coins
      const vipBonusCoins = vipCoinBonus; // Topaz Token: direct coin bonus
      
      // Apply VIP score boost
      const boostedScore = Math.floor(totalScore * vipScoreMultiplier);
      
      // Premium membership benefits
      const isPremiumMember = membershipStatus?.is_premium_member || false;
      const memberXPMultiplier = isPremiumMember ? 1.2 : 1; // 1.2x XP for members
      const memberCoinMultiplier = isPremiumMember ? 2 : 1; // 2x coins for members
      
      // XP gained equals Total Score (with 2X multiplier if active, + 1.2x for premium, + VIP score boost)
      const xpMultiplier = (doubleXPActive ? 2 : 1) * memberXPMultiplier * vipScoreMultiplier;
      const xpGained = Math.floor(boostedScore * xpMultiplier / vipScoreMultiplier); // Don't double-apply vip
      
      // Track XP earned for daily challenges
      updateDailyProgress('total_xp', xpGained);
      
      // Calculate and award stars (only new stars are awarded)
      const earnedStars = getLevelStars(currentLevelId, boostedScore);
      const newStarsAwarded = awardStars(currentLevelId, earnedStars);
      
      // Calculate and award coins based on stars and score
      // Formula: Base coins by stars + score bonus per 1000 points
      // With: premium members (2x), Emerald Chalice (2x), Topaz Token (bonus)
      const baseCoinsEarned = calculateCoins(earnedStars, boostedScore);
      const vipCoinMultiplier = vipDoubleCoinActive ? 2 : 1;
      const coinsEarned = Math.floor(baseCoinsEarned * memberCoinMultiplier * vipCoinMultiplier) + vipBonusCoins;
      awardCoins(coinsEarned);
      
      // Check for new fruit unlock (show notification after a delay)
      setTimeout(() => {
        checkAndShowUnlock(currentLevelId);
      }, 2000); // Show after win modal appears
      
      // Check for level up before adding XP
      const levelUpInfo = checkLevelUp(xpGained);
      const previousXP = totalXP;
      const previousLevel = level;
      
      // Add XP
      addXP(xpGained);
      
      // Calculate new XP needed for next level
      const newLevel = levelUpInfo.newLevel;
      const newNeededXP = getXPForNextLevel(newLevel);
      
      // === Battle Pass XP Tracking ===
      // Award Battle Pass XP: 10% of game score + 10% premium bonus
      const addBattlePassXPAsync = async () => {
        try {
          // Pass the raw game score (before moves bonus), BP XP is 10% of this
          const bpResult = await addBattlePassXP(score);
          
          // Update animation data with Battle Pass info
          setXpAnimationData(prev => ({
            ...prev,
            battlePassXP: bpResult?.total_xp_earned || 0,
            battlePassBonusXP: bpResult?.bonus_xp || 0,
            isPremium: bpResult?.is_premium || false,
            newBPTier: bpResult?.new_tier || 0,
            tierUp: bpResult?.tier_up || false
          }));
        } catch (error) {
          console.error('Failed to add Battle Pass XP:', error);
        }
      };
      addBattlePassXPAsync();

      // === Tournament score submission ===
      const TOURNAMENT_LEVELS = [50, 100, 150, 175, 200];
      if (TOURNAMENT_LEVELS.includes(currentLevelId)) {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('fruitCrush_token');
        if (token) {
          fetch(`${apiUrl}/api/tournament/submit-score`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ level_id: currentLevelId, score: boostedScore }),
          }).catch(() => {});
        }
      }
      
      // Store XP animation data for win modal
      setXpAnimationData({
        xpGained,
        previousXP,
        newXP: previousXP + xpGained,
        previousLevel,
        newLevel,
        neededXP: newNeededXP,
        starsEarned: earnedStars,
        newStarsAwarded: newStarsAwarded,
        coinsEarned: coinsEarned, // Coins earned this level (with premium bonus if applicable)
        xpMultiplier: xpMultiplier, // Add multiplier info for display
        isPremiumMember: isPremiumMember, // Track premium membership for UI
        memberXPBonus: isPremiumMember ? Math.floor(totalScore * 0.2 * (doubleXPActive ? 2 : 1)) : 0, // Bonus XP from membership
        memberCoinBonus: isPremiumMember ? baseCoinsEarned : 0, // Bonus coins from membership
        battlePassXP: 0, // Will be updated async
        battlePassBonusXP: 0,
        isPremium: battlePass?.is_premium || false,
      });
      
      // Trigger level up haptic if leveled up
      if (levelUpInfo.leveledUp) {
        setTimeout(() => {
          haptic.levelUp();
          playSound('levelUp');
        }, 1500);
      }
      
      saveLevelScore(currentLevelId, totalScore);
      if (user?.playerName) {
        updateLeaderboard(user.playerName, totalScore);
      }
      
      // Check for item drops from level completion
      const checkForDrops = async () => {
        try {
          const token = localStorage.getItem('fruitCrush_token');
          if (!token) return;
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/levels/complete`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              level_id: currentLevelId,
              score: totalScore,
              stars: earnedStars,
              fruits_collected: fruitsCollected || 0,
              cascade_count: cascadeCount || 0,
              is_boss_level: isBossLevel,
              boss_won: isBossLevel && gameStatus === 'won'
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.drops && data.drops.length > 0) {
              setItemDrops(data.drops);
            }
          }
        } catch (error) {
          console.error('Failed to check for drops:', error);
        }
      };
      checkForDrops();

      // Submit PvP score if this is a PvP match
      if (pvpData) {
        const submitPvPScore = async () => {
          try {
            const token = localStorage.getItem('fruitCrush_token');
            if (!token) return;
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pvp/submit-score`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({
                challenge_id: pvpData.challengeId,
                score: totalScore,
                moves_left: movesLeft
              })
            });
            const data = await res.json();
            if (data.completed) {
              setPvpResult(data);
            } else if (data.success) {
              setPvpResult({ submitted: true, message: data.message, your_score: data.your_score });
            }
          } catch (err) {
            console.error('Failed to submit PvP score:', err);
          }
        };
        submitPvPScore();
      }
    } else if (gameStatus === 'lost' && !hasProcessedLossRef.current) {
      playSound('lose');
      haptic.lose();
      // Lose a life
      takeDamage();
      hasProcessedLossRef.current = true;
      // Save score even on loss
      if (user?.playerName && score > 0) {
        updateLeaderboard(user.playerName, score);
      }
      // Submit PvP score on loss
      if (pvpData) {
        const submitPvPLoss = async () => {
          try {
            const token = localStorage.getItem('fruitCrush_token');
            if (!token) return;
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pvp/submit-score`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ challenge_id: pvpData.challengeId, score: score, moves_left: 0 })
            });
            const data = await res.json();
            if (data.completed) setPvpResult(data);
            else if (data.success) setPvpResult({ submitted: true, message: data.message, your_score: score });
          } catch (err) { console.error('Failed to submit PvP score:', err); }
        };
        submitPvPLoss();
      }
    } else if (gameStatus === 'playing') {
      hasProcessedLossRef.current = false;
      hasProcessedWinRef.current = false;
      setXpAnimationData(null);
    }
  }, [gameStatus, playSound, haptic, score, movesLeft, user?.playerName, currentLevelId, takeDamage, addXP, checkLevelUp, totalXP, level, addLevelCompleted, updateDailyProgress, addBattlePassXP, battlePass]);

  const handleResetWithSound = useCallback(() => {
    // Show confirmation dialog instead of directly restarting
    setShowRestartConfirm(true);
  }, []);
  
  const confirmRestart = useCallback(() => {
    // Restarting costs 1 life
    if (!canPlay) {
      navigate("/map");
      return;
    }
    takeDamage();
    playSound('select');
    hasProcessedLossRef.current = false;
    hasProcessedWinRef.current = false;
    resetGame();
    setShowRestartConfirm(false);
  }, [resetGame, playSound, takeDamage, canPlay, navigate]);
  
  const cancelRestart = useCallback(() => {
    setShowRestartConfirm(false);
  }, []);
  
  // Haptic feedback for explosions
  useEffect(() => {
    if (explodingCells && explodingCells.size > 0) {
      if (explodingCells.size >= 8) {
        haptic.explosion();
      } else if (explodingCells.size >= 5) {
        haptic.combo();
      } else if (explodingCells.size >= 4) {
        haptic.special();
      } else {
        haptic.match();
      }
    }
  }, [explodingCells, haptic]);
  
  // Haptic for power-ups
  useEffect(() => {
    if (showDoublePointsAlert) {
      haptic.special();
    }
  }, [showDoublePointsAlert, haptic]);
  
  useEffect(() => {
    if (showInstaEliminationPicker) {
      haptic.combo();
    }
  }, [showInstaEliminationPicker, haptic]);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const handleBackToMap = useCallback(() => {
    navigate("/map");
  }, [navigate]);

  // Render the correct boss visual based on boss type
  const renderBossVisual = useCallback(() => {
    if (!bossConfig) return null;
    
    const visualProps = {
      isAttacking: bossFight.isAttacking,
      currentPhase: bossFight.currentPhase,
      isDamaged: bossFight.isDamaged,
      attackType: bossFight.phase?.attack
    };
    
    const bossType = bossConfig.type || 'dragon';
    
    // Match boss types to their visual components
    switch(bossType) {
      case 'owl':
        return <OwlGuardianVisual {...visualProps} />;
      case 'kraken':
        return <KrakenVisual {...visualProps} />;
      case 'phoenix':
      case 'phoenix_final':
        return <PhoenixVisual {...visualProps} />;
      case 'dream_weaver':
      case 'twilight':
        return <DreamWeaverVisual {...visualProps} />;
      case 'alchemist':
      case 'transmuter':
        return <AlchemistVisual {...visualProps} />;
      case 'ringmaster':
      case 'circus_king':
        return <RingmasterVisual {...visualProps} />;
      case 'blackfin':
      case 'kraken_wrath':
        return <PirateCaptainVisual {...visualProps} />;
      case 'temple_guardian':
      case 'zen_master':
        return <TempleGuardianVisual {...visualProps} />;
      case 'thunder_lord':
      case 'storm_king':
        return <StormKingVisual {...visualProps} />;
      case 'spore_lord':
      case 'fungal_king':
        return <FungalKingVisual {...visualProps} />;
      case 'dealer':
      case 'jackpot_king':
        return <CasinoDealerVisual {...visualProps} />;
      case 'void_walker':
      case 'celestial_dragon':
        return <CosmicEntityVisual {...visualProps} />;
      case 'pharaoh':
      case 'pharaoh_guardian':
        return <PharaohVisual {...visualProps} />;
      case 'frost_giant':
        return <FrostGiantVisual {...visualProps} />;
      default:
        return <DragonVisual image={bossConfig.image} {...visualProps} />;
    }
  }, [bossConfig, bossFight]);

  // Handle exit with warning when game is in progress
  const handleExitAttempt = useCallback(() => {
    // Only show warning if game is still playing (not won or lost)
    if (gameStatus === 'playing') {
      setShowExitWarning(true);
    } else {
      navigate("/map");
    }
  }, [gameStatus, navigate]);

  // Confirm exit - lose HP and go back (free for boss levels)
  const handleConfirmExit = useCallback(() => {
    if (!isBossLevel) {
      takeDamage();
    }
    setShowExitWarning(false);
    navigate("/map");
  }, [takeDamage, navigate, isBossLevel]);

  // Cancel exit - continue playing
  const handleCancelExit = useCallback(() => {
    setShowExitWarning(false);
  }, []);
  
  // Handle boss victory continue
  const handleBossVictoryContinue = useCallback(async () => {
    // Award stars
    if (levelConfig?.rewards?.stars) {
      awardStars(levelConfig.rewards.stars);
    }
    
    // Award XP
    if (levelConfig?.rewards?.xp) {
      addXP(levelConfig.rewards.xp);
    }
    
    // Unlock frame if applicable
    if (levelConfig?.rewards?.frame) {
      // Could add frame to user's unlocked frames here
    }
    
    // Mark level as completed
    markLevelCompleted(currentLevelId);
    addLevelCompleted();

    // === Tournament score submission (boss levels) ===
    const TOURNAMENT_LEVELS = [50, 100, 150, 175, 200];
    if (TOURNAMENT_LEVELS.includes(currentLevelId)) {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('fruitCrush_token');
      if (token) {
        fetch(`${apiUrl}/api/tournament/submit-score`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ level_id: currentLevelId, score: score }),
        }).catch(() => {});
      }
    }

    setShowBossVictory(false);
    navigate("/map");
  }, [levelConfig, awardStars, addXP, currentLevelId, navigate, addLevelCompleted, score]);

  const handleNextLevel = useCallback(() => {
    const nextLevelId = currentLevelId + 1;
    if (nextLevelId <= LEVELS.length) {
      navigate(`/play/${nextLevelId}`);
      hasProcessedLossRef.current = false;
      hasProcessedWinRef.current = false;
      setXpAnimationData(null);
      resetGame();
    } else {
      navigate("/map");
    }
  }, [currentLevelId, navigate, resetGame]);

  const handlePlayAgain = useCallback(() => {
    if (canPlay) {
      hasProcessedLossRef.current = false;
      hasProcessedWinRef.current = false;
      setXpAnimationData(null);
      resetGame();
    } else {
      navigate("/map");
    }
  }, [canPlay, resetGame, navigate]);

  // Build goal text for header
  const goalText = levelConfig.missionType 
    ? getMissionDescription(levelConfig)
    : `Collect ${levelConfig.targetCount} ${levelConfig.targetEmoji}`;

  return (
    <div className="min-h-screen game-gradient-bg pattern-dots pb-24">
      {/* Mobile-Optimized Header */}
      <GameHeader
        levelName={levelConfig.name}
        levelSubtitle={levelConfig.subtitle}
        goal={goalText}
        moves={levelConfig.moves}
        movesLeft={movesLeft}
        onBack={handleExitAttempt}
        score={score}
        targetEmoji={levelConfig.targetEmoji}
        targetCount={levelConfig.targetCount}
        fruitsCollected={fruitsCollected}
        ghostScore={ghostScore}
      />

      {/* PvP Banner */}
      {pvpData && (
        <div className="fixed top-[60px] left-0 right-0 z-30 bg-gradient-to-r from-red-600 to-orange-600 py-1.5 px-4 flex items-center justify-center gap-2" data-testid="pvp-banner">
          <Swords className="w-4 h-4 text-white" />
          <span className="text-white text-xs font-bold">PvP Challenge</span>
          <span className="text-white/80 text-xs">| 🪙 {(pvpData.wager * 2).toLocaleString()} pot</span>
        </div>
      )}

      {/* PvP Result Overlay */}
      {pvpResult && pvpResult.completed && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="pvp-result-overlay">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full text-center space-y-4">
            {pvpResult.winner_name ? (
              <>
                <div className="text-5xl mb-2">{pvpResult.winner_id ? '🏆' : '🤝'}</div>
                <h2 className="text-2xl font-bold text-white">{pvpResult.winner_name} Wins!</h2>
                <p className="text-green-400 font-bold text-lg">🪙 {pvpResult.total_pot?.toLocaleString()} coins</p>
              </>
            ) : (
              <>
                <div className="text-5xl mb-2">🤝</div>
                <h2 className="text-2xl font-bold text-white">Perfect Tie!</h2>
                <p className="text-slate-300">Coins refunded to both players</p>
              </>
            )}
            <div className="bg-slate-800 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between text-white">
                <span>{pvpResult.creator_name || 'Creator'}</span>
                <span className="font-bold">{pvpResult.creator_score?.toLocaleString()} <span className="text-slate-400 text-xs">({pvpResult.creator_moves_left} moves left)</span></span>
              </div>
              <div className="flex justify-between text-white">
                <span>{pvpResult.opponent_name || 'Opponent'}</span>
                <span className="font-bold">{pvpResult.opponent_score?.toLocaleString()} <span className="text-slate-400 text-xs">({pvpResult.opponent_moves_left} moves left)</span></span>
              </div>
            </div>
            <Button onClick={() => navigate('/map')} className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold" data-testid="pvp-result-back">
              Back to Map
            </Button>
          </div>
        </div>
      )}

      {/* PvP Score Submitted (waiting for opponent) */}
      {pvpResult && pvpResult.submitted && !pvpResult.completed && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-md flex items-center justify-center p-4" data-testid="pvp-submitted-overlay">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full text-center space-y-4">
            <div className="text-5xl mb-2">⏳</div>
            <h2 className="text-xl font-bold text-white">Score Submitted!</h2>
            <p className="text-slate-300 text-sm">{pvpResult.message}</p>
            <p className="text-yellow-400 font-bold">Your score: {pvpResult.your_score?.toLocaleString()}</p>
            <Button onClick={() => navigate('/map')} className="w-full bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold" data-testid="pvp-submitted-back">
              Back to Map
            </Button>
          </div>
        </div>
      )}

      <div className="container mx-auto px-3 pt-24 max-w-lg">
        {/* BOSS FIGHT UI - Compact */}
        {isBossLevel && bossConfig && (
          <div className={`mb-3 space-y-2 ${bossFight.isAttacking ? 'screen-shake' : ''}`}>
            {/* Boss Visual - Smaller */}
            <div className="flex justify-center scale-90">
              {renderBossVisual()}
            </div>
            
            {/* Boss HP Bar - Compact */}
            <BossHPBar 
              currentHP={bossFight.currentHP}
              maxHP={bossFight.maxHP}
              phase={bossFight.currentPhase}
              totalPhases={bossConfig.phases.length}
              bossName={bossConfig.name}
              isAttacking={bossFight.isAttacking}
              weakSpot={bossFight.phase?.weakEmoji}
            />
            
            {/* Attack Warning - Inline */}
            <AttackWarning 
              attackType={bossFight.phase?.attack}
              movesUntilAttack={bossFight.movesUntilAttack}
              isAttacking={bossFight.isAttacking}
              bossType={bossConfig.type || 'dragon'}
            />
            
            {/* Shield Blocked Animation - Enhanced with bubble effect */}
            {bossFight.attackBlocked && (
              <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none overflow-hidden">
                {/* Shield bubble expanding effect */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-64 h-64 rounded-full border-4 border-yellow-400 animate-shield-expand opacity-60" />
                  <div className="absolute w-48 h-48 rounded-full border-4 border-amber-300 animate-shield-expand animation-delay-100" />
                  <div className="absolute w-32 h-32 rounded-full border-4 border-yellow-200 animate-shield-expand animation-delay-200" />
                </div>
                
                {/* Sparkle particles */}
                {[...Array(12)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute text-2xl animate-sparkle-burst"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `rotate(${i * 30}deg) translateY(-80px)`,
                      animationDelay: `${i * 0.05}s`
                    }}
                  >
                    ✨
                  </div>
                ))}
                
                {/* Main message */}
                <div className="relative bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 px-8 py-4 rounded-2xl shadow-2xl animate-shield-blocked z-10">
                  <div className="absolute inset-0 bg-white/20 rounded-2xl animate-pulse" />
                  <div className="flex items-center gap-3 text-white font-bold text-xl relative">
                    <span className="text-3xl animate-bounce">🛡️</span>
                    <span className="drop-shadow-lg">ATTACK BLOCKED!</span>
                    <span className="text-3xl animate-bounce animation-delay-100">✨</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Shield active glow effect around boss area */}
            {guardianShieldActive && !bossFight.attackBlocked && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 border-4 border-yellow-400/30 rounded-xl animate-pulse" />
                <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400/10 via-transparent to-yellow-400/10 rounded-xl animate-shield-shimmer" />
              </div>
            )}
          </div>
        )}

        {/* Dragon Scale Fire Effect - Shows during boss damage */}
        {dragonScaleActive && isBossLevel && (
          <div className="fixed inset-0 pointer-events-none z-40 overflow-hidden">
            {/* Fire particles from edges */}
            {[...Array(20)].map((_, i) => (
              <div
                key={`fire-${i}`}
                className="absolute text-2xl animate-fire-rise"
                style={{
                  left: `${5 + (i * 5)}%`,
                  bottom: '-20px',
                  animationDelay: `${i * 0.15}s`,
                  animationDuration: `${1.5 + Math.random()}s`
                }}
              >
                🔥
              </div>
            ))}
            {/* Side flames */}
            <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-orange-500/20 to-transparent animate-pulse" />
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-orange-500/20 to-transparent animate-pulse" />
          </div>
        )}
        
        {/* Dragon Scale Hit Burst - Shows when damage is applied with Dragon Scale */}
        {showDragonScaleHit && (
          <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center overflow-hidden">
            {/* Central fire explosion */}
            <div className="relative">
              {/* Fire burst particles */}
              {[...Array(16)].map((_, i) => (
                <div
                  key={`burst-${i}`}
                  className="absolute text-4xl"
                  style={{
                    animation: 'fire-burst 0.8s ease-out forwards',
                    transform: `rotate(${i * 22.5}deg)`,
                    transformOrigin: 'center center'
                  }}
                >
                  <span style={{ 
                    display: 'inline-block',
                    animation: `fire-particle ${0.4 + Math.random() * 0.4}s ease-out forwards`,
                    animationDelay: `${i * 0.03}s`
                  }}>
                    {i % 2 === 0 ? '🔥' : '💥'}
                  </span>
                </div>
              ))}
              
              {/* Central impact */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-6xl animate-ping">💥</div>
              </div>
              
              {/* Damage text */}
              <div className="absolute -top-16 left-1/2 -translate-x-1/2 whitespace-nowrap">
                <span className="text-3xl font-black text-orange-500 drop-shadow-lg animate-bounce">
                  🐉 DRAGON SCALE BONUS! 🐉
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Power-Up Indicators - Compact row above board */}
        {(doublePointsActive || doubleXPActive || instaEliminationActive || guardianShieldActive || dragonScaleActive || moonStoneActive || oceanPearlActive) && (
          <div className="flex justify-center items-center gap-2 mb-2 flex-wrap">
            <DoublePointsIndicator isActive={doublePointsActive} />
            <DoubleXPIndicator isActive={doubleXPActive} turnsLeft={doubleXPTurnsLeft} />
            <InstaEliminationIndicator 
              isActive={instaEliminationActive} 
              onClick={openInstaEliminationPicker} 
            />
            {/* Guardian Shield Active Indicator */}
            {guardianShieldActive && (
              <div className="relative flex items-center gap-1 bg-gradient-to-r from-yellow-500 to-amber-500 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 animate-shield-shimmer" />
                <span className="text-lg animate-bounce">🛡️</span>
                <span className="relative">Shield Ready</span>
                <span className="text-lg animate-pulse">✨</span>
              </div>
            )}
            {/* Dragon Scale Active Indicator */}
            {dragonScaleActive && (
              <div className="relative flex items-center gap-1 bg-gradient-to-r from-red-600 via-orange-500 to-red-600 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg animate-dragon-pulse overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-orange-400/30 to-transparent animate-pulse" />
                <span className="text-lg">🐉</span>
                <span className="relative font-extrabold">+50% DMG</span>
                <span className="text-lg">🔥</span>
              </div>
            )}
            {/* Moon Stone Active Indicator */}
            {moonStoneActive && (
              <div className="relative flex items-center gap-1 bg-gradient-to-r from-indigo-600 via-purple-500 to-indigo-600 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg animate-moon-glow overflow-hidden">
                <span className="text-lg animate-pulse">🌙</span>
                <span className="relative">Best Moves</span>
                <span className="text-lg animate-bounce">✨</span>
              </div>
            )}
            {/* Ocean Pearl Active Indicator */}
            {oceanPearlActive && (
              <div className="relative flex items-center gap-1 bg-gradient-to-r from-cyan-500 via-blue-400 to-cyan-500 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg animate-ocean-wave overflow-hidden">
                <span className="text-lg">🌊</span>
                <span className="relative">Pearl Shield</span>
                <span className="text-lg animate-pulse">🫧</span>
              </div>
            )}
          </div>
        )}

        {/* World Mechanic Indicators */}
        {worldMechanic && (
          <div className="flex justify-center items-center gap-2 mb-2 flex-wrap">
            {/* Volcanic Core - Lava Rise Indicator */}
            {worldMechanic === 'lava_rise' && (
              <div className="flex items-center gap-2 bg-gradient-to-r from-red-700 via-orange-600 to-red-700 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg">
                <span className="text-lg">🌋</span>
                <span>Lava: {lavaRiseRate - movesSinceLavaRise} moves</span>
                <div className="flex gap-0.5">
                  {[...Array(Math.min(lavaLevel, 5))].map((_, i) => (
                    <div key={i} className="w-2 h-3 bg-orange-400 rounded-sm animate-pulse" />
                  ))}
                </div>
              </div>
            )}
            
            {/* Moonlight Garden - Day/Night Indicator */}
            {worldMechanic === 'day_night' && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg transition-all duration-500 ${
                isDayTime 
                  ? 'bg-gradient-to-r from-yellow-500 via-orange-400 to-yellow-500' 
                  : 'bg-gradient-to-r from-indigo-700 via-purple-600 to-indigo-700'
              }`}>
                <span className={`text-lg ${dayNightTransition ? 'animate-spin' : ''}`}>
                  {isDayTime ? '☀️' : '🌙'}
                </span>
                <span>{isDayTime ? 'Day' : 'Night'}: {cycleLength - movesSinceCycleChange} moves</span>
              </div>
            )}
            
            {/* Cosmic Nebula - Gravity Indicator */}
            {worldMechanic === 'gravity_shift' && (
              <div className={`flex items-center gap-2 bg-gradient-to-r from-purple-700 via-indigo-600 to-purple-700 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg ${
                gravityShiftPending ? 'animate-pulse' : ''
              }`}>
                <span className="text-lg">🌌</span>
                <span>Gravity:</span>
                <span className="text-xl">
                  {gravityDirection === 'down' && '⬇️'}
                  {gravityDirection === 'up' && '⬆️'}
                  {gravityDirection === 'left' && '⬅️'}
                  {gravityDirection === 'right' && '➡️'}
                </span>
              </div>
            )}
            
            {/* Storm Kingdom - Lightning Warning */}
            {worldMechanic === 'lightning_strikes' && (
              <div className={`flex items-center gap-2 bg-gradient-to-r from-gray-700 via-yellow-600 to-gray-700 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg ${
                showLightning ? 'animate-pulse bg-yellow-500' : ''
              }`}>
                <span className={`text-lg ${showLightning ? 'animate-bounce' : ''}`}>⚡</span>
                <span>{showLightning ? 'STRIKE!' : 'Storm Active'}</span>
              </div>
            )}
            
            {/* Casino Royale - Multiplier Display */}
            {worldMechanic === 'random_multipliers' && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-white text-xs font-bold shadow-lg ${
                activeMultiplier >= 5 
                  ? 'bg-gradient-to-r from-yellow-500 via-amber-400 to-yellow-500 animate-pulse' 
                  : activeMultiplier >= 3
                    ? 'bg-gradient-to-r from-green-500 via-emerald-400 to-green-500'
                    : 'bg-gradient-to-r from-gray-600 via-gray-500 to-gray-600'
              }`}>
                <span className="text-lg">🎰</span>
                <span className="text-lg font-black">{activeMultiplier}X</span>
                {showJackpot && <span className="text-lg animate-bounce">💰</span>}
              </div>
            )}
          </div>
        )}

        {/* Lightning Strike Overlay */}
        {showLightning && lightningCells.length > 0 && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {/* Flash effect */}
            <div className="absolute inset-0 bg-yellow-200/30 animate-pulse" />
            {/* Lightning bolts */}
            {lightningCells.map((cell, i) => (
              <div 
                key={i}
                className="absolute text-6xl animate-bounce"
                style={{
                  left: `${15 + (cell.col * 8)}%`,
                  top: `${20 + (cell.row * 6)}%`,
                }}
              >
                ⚡
              </div>
            ))}
          </div>
        )}

        {/* Day/Night Transition Overlay */}
        {dayNightTransition && (
          <div className={`fixed inset-0 pointer-events-none z-30 transition-opacity duration-1000 ${
            isDayTime ? 'bg-yellow-300/20' : 'bg-indigo-900/30'
          }`}>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-8xl animate-spin">
                {isDayTime ? '☀️' : '🌙'}
              </span>
            </div>
          </div>
        )}

        {/* Jackpot Celebration */}
        {showJackpot && (
          <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
            <div className="text-center animate-bounce">
              <div className="text-6xl mb-2">🎰💰🎰</div>
              <div className="text-4xl font-black text-yellow-400 drop-shadow-lg">
                JACKPOT!
              </div>
            </div>
            {/* Coins falling */}
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute text-3xl animate-fall"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: '-10%',
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: '2s',
                }}
              >
                🪙
              </div>
            ))}
          </div>
        )}

        {/* Mission Progress - Only for special missions (not collect_fruit which is in header) */}
        {levelConfig.missionType && levelConfig.missionType !== 'collect_fruit' && (
          <div className="flex justify-center mb-2">
            <MissionProgress
              levelConfig={levelConfig}
              missionProgress={missionProgress}
              fruitsCollected={fruitsCollected}
              dualFruitsCollected={dualFruitsCollected}
              score={score}
              className="w-full max-w-xs"
            />
          </div>
        )}
        
        {/* VIP Items Bar (shown on right side during gameplay) */}
        {gameStatus === 'playing' && (
          <VIPItemsBar
            inventory={vipItems.inventory}
            onUseItem={handleUseVIPItem}
            disabled={isAnimating || !!vipItemPending}
            activeEffects={vipItems.activeEffects}
          />
        )}
        
        {/* VIP Active Effect Indicators */}
        {(vipItems.activeEffects.score_boost || vipItems.activeEffects.double_coins || vipItems.activeEffects.match_highlight || vipItems.activeEffects.boss_damage_boost) && (
          <div className="flex justify-center gap-2 mb-2 flex-wrap">
            {vipItems.activeEffects.score_boost && (
              <VIPEffectIndicator 
                effect="score_boost" 
                turnsLeft={vipItems.activeEffects.score_boost.turnsLeft} 
              />
            )}
            {vipItems.activeEffects.double_coins && (
              <VIPEffectIndicator effect="double_coins" />
            )}
            {vipItems.activeEffects.match_highlight && (
              <VIPEffectIndicator 
                effect="match_highlight" 
                timeLeft={vipItems.activeEffects.match_highlight.timeLeft} 
              />
            )}
            {vipItems.activeEffects.boss_damage_boost && (
              <VIPEffectIndicator effect="boss_damage_boost" />
            )}
          </div>
        )}
        
        {/* VIP Item pending - waiting for board selection */}
        {vipItemPending && (
          <div className="flex justify-center mb-2">
            <div className="bg-amber-500/20 border border-amber-400 rounded-full px-4 py-2 flex items-center gap-2 animate-pulse">
              <span className="text-xl">{VIP_ITEMS_CONFIG[vipItemPending.id]?.emoji}</span>
              <span className="text-amber-300 font-bold text-sm">Tap a cell to activate!</span>
              <button 
                onClick={() => setVipItemPending(null)}
                className="ml-2 text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Game Board */}
        <div className={`flex justify-center my-2 relative ${screenShake ? 'animate-screen-shake' : ''}`}>
          {/* Fruity Crush! Text Overlay */}
          {showFruityCrush && (
            <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none">
              <div className="fruity-crush-text animate-fruity-crush-enter text-4xl sm:text-5xl md:text-6xl font-black">
                🍇 FRUITY CRUSH! 🍓
              </div>
            </div>
          )}
          
          {/* Dynamic Combo Name Display */}
          {showComboName && comboName && (
            <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none">
              <div className={`animate-combo-bounce bg-gradient-to-r ${comboName.color} text-white font-black text-2xl sm:text-3xl md:text-4xl px-6 py-3 rounded-2xl shadow-2xl border-4 border-white/30`}>
                <span className="mr-2">{comboName.emoji}</span>
                <span className="drop-shadow-lg">{comboName.name}</span>
              </div>
            </div>
          )}
          
          {/* Combo Multiplier Display */}
          {showComboMultiplier && cascadeCount > 1 && (
            <div className="absolute top-4 right-4 z-50 pointer-events-none">
              <div className="animate-combo-slide-in bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-black text-2xl sm:text-3xl px-4 py-2 rounded-full shadow-lg">
                {cascadeCount}X COMBO!
              </div>
            </div>
          )}
          
          {/* Low Moves Warning */}
          {movesLeft <= 5 && movesLeft > 0 && gameStatus === 'playing' && (
            <div className="absolute inset-0 animate-urgency-pulse pointer-events-none rounded-xl z-40" />
          )}
          
          <GameBoard
            board={board}
            selectedCell={selectedCell}
            onCellClick={handleCellClickWithSound}
            onSwipe={handleSwipeWithSound}
            isAnimating={isAnimating}
            explodingCells={explodingCells}
            clearEffect={clearEffect}
            boardSize={boardSize}
            swappingCells={swappingCells}
            swordMode={swordMode}
            swordTier={swordTier}
            onSwordUse={useSwordAbility}
            swordSlashAnimation={swordSlashAnimation}
            specialComboAnimation={specialComboAnimation}
            bombMode={bombMode}
            colorBombMode={colorBombMode}
            wrappedMode={wrappedMode}
            discoMode={discoMode}
            stripedMode={stripedMode}
            hintCell={hintCell}
            hintCells={advancedHintCells}
            equippedSkin={equippedSkin}
            equippedEffect={equippedEffect}
            equippedTheme={equippedTheme}
            godSwordMode={godSwordMode}
            godSwordAnimation={godSwordAnimation}
            onGodSwordUse={handleGodSwordUse}
            lavaLevel={lavaLevel}
            worldMechanic={worldMechanic}
            comboCount={cascadeCount}
          />

          {/* Score Pop-ups */}
          {scorePopups.map((popup) => (
            <div
              key={popup.id}
              className="absolute pointer-events-none z-50 animate-score-pop"
              style={{
                left: `${(popup.x / boardSize) * 100}%`,
                top: `${(popup.y / boardSize) * 100}%`,
              }}
              onAnimationEnd={() => setScorePopups(prev => prev.filter(p => p.id !== popup.id))}
            >
              <span className={`font-black text-lg drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] ${
                popup.type === 'mastery' ? 'text-purple-300' : 'text-yellow-300'
              }`}>
                +{popup.value}
              </span>
            </div>
          ))}
        </div>
        
        {/* Crystal Ball Preview - Shows next 3 rows of falling fruits */}
        {crystalBallActive && crystalBallPreview && crystalBallPreview.length > 0 && (
          <div className="fixed top-20 right-4 z-50 animate-slide-in-right">
            <div className="bg-purple-900/95 backdrop-blur-md rounded-xl p-3 border-2 border-purple-400 shadow-2xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🔮</span>
                <span className="text-white font-bold text-sm">Crystal Ball Preview</span>
              </div>
              <div className="space-y-1">
                {crystalBallPreview.map((row, rowIdx) => (
                  <div key={rowIdx} className="flex gap-0.5">
                    {row.map((cell, colIdx) => (
                      <div 
                        key={colIdx}
                        className="w-6 h-6 flex items-center justify-center bg-purple-800/50 rounded text-sm"
                      >
                        {cell.emoji}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="text-purple-300 text-xs mt-2 text-center">
                Next fruits preview (10s)
              </div>
            </div>
          </div>
        )}

        {/* Active Mode Indicators - Only show when a mode is active */}
        {(bombMode || colorBombMode || wrappedMode || discoMode || stripedMode || swordMode || godSwordMode) && (
          <div className="flex justify-center mt-2">
            {bombMode && (
              <button
                onClick={cancelBombMode}
                className="px-4 py-2 rounded-full bg-red-500 text-white font-bold animate-pulse flex items-center gap-2"
              >
                💣 Tap board to bomb! <span className="text-xs opacity-80">(tap to cancel)</span>
              </button>
            )}
            {colorBombMode && (
              <button
                onClick={cancelColorBombMode}
                className="px-4 py-2 rounded-full bg-purple-500 text-white font-bold animate-pulse flex items-center gap-2"
              >
                🌈 Tap a fruit! <span className="text-xs opacity-80">(tap to cancel)</span>
              </button>
            )}
            {wrappedMode && (
              <button
                onClick={cancelWrappedMode}
                className="px-4 py-2 rounded-full bg-pink-500 text-white font-bold animate-pulse flex items-center gap-2"
              >
                🎁 Place wrapped! <span className="text-xs opacity-80">(tap to cancel)</span>
              </button>
            )}
            {discoMode && (
              <button
                onClick={cancelDiscoMode}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 text-white font-bold animate-pulse flex items-center gap-2"
              >
                🪩 Place disco! <span className="text-xs opacity-80">(tap to cancel)</span>
              </button>
            )}
            {stripedMode && (
              <button
                onClick={cancelStripedMode}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold animate-pulse flex items-center gap-2"
              >
                🍬 Place striped! <span className="text-xs opacity-80">(tap to cancel)</span>
              </button>
            )}
            {swordMode && swordTier && (
              <button
                onClick={cancelSwordMode}
                className="px-4 py-2 rounded-full bg-amber-500 text-white font-bold animate-pulse flex items-center gap-2"
              >
                ⚔️ Tap to slash {swordTier.size}x{swordTier.size}! <span className="text-xs opacity-80">(tap to cancel)</span>
              </button>
            )}
            {godSwordMode && (
              <button
                onClick={cancelGodSwordMode}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-bold animate-pulse flex items-center gap-2"
              >
                ⚔️ Tap to clear board! <span className="text-xs opacity-80">(tap to cancel)</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Floating Menu - Right side */}
      <GameFloatingMenu
        onSettings={() => setShowSettings(true)}
        onHelp={() => setShowHowToPlay(true)}
        onRestart={handleResetWithSound}
        onLevelMap={handleExitAttempt}
        onInventory={() => setShowInventory(true)}
        onToggleHaptic={haptic.toggleHaptic}
        hapticEnabled={haptic.enabled}
        hapticSupported={haptic.supported}
        hp={hp}
        inventoryCount={totalInventoryItems}
        swordTier={swordTier}
        swordMode={swordMode}
        swordUsesRemaining={swordUsesRemaining}
        maxSwordUses={maxSwordUses}
        onSwordToggle={swordMode ? cancelSwordMode : activateSwordMode}
        onOpenSwordSelection={() => setShowSwordSelection(true)}
        hasGodSword={hasGodSword}
        godSwordMode={godSwordMode}
        godSwordUsesRemaining={godSwordUsesRemaining}
        onGodSwordToggle={godSwordMode ? cancelGodSwordMode : activateGodSwordMode}
        disabled={gameStatus !== 'playing'}
      />

      {/* Always-visible Hint Button - Center bottom */}
      <GameHintButton
        onClick={() => {
          showAdvancedHint();
          playSound('select');
        }}
        disabled={gameStatus !== 'playing' || isAnimating}
      />

      {/* Sword Selection Modal */}
      <SwordSelectionModal
        isOpen={showSwordSelection}
        onClose={() => setShowSwordSelection(false)}
        onSelectSword={(swordId) => {
          // Store the selected sword and activate sword mode
          localStorage.setItem('fruitCrush_equippedSword', swordId);
          activateSwordMode();
          playSound('select');
        }}
        onSelectGodSword={() => {
          activateGodSwordMode();
          playSound('select');
        }}
        onRechargeGodSword={async () => {
          // Recharge god sword via API
          const result = await rechargeEquipment('god_sword');
          if (result?.success) {
            initializeGodSwordUses(result.uses_remaining || 5);
            playSound('powerup');
          }
        }}
        currentLevel={highestCompletedLevel}
        equippedSword={equippedSword}
        swordUsesRemaining={swordUsesRemaining}
        maxSwordUses={maxSwordUses}
        hasGodSword={hasGodSword}
        godSwordUsesRemaining={godSwordUsesRemaining}
        disabled={gameStatus !== 'playing'}
      />

      {/* Restart Confirmation Dialog */}
      {showRestartConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-bounce-in">
            <div className="text-center">
              <div className="text-5xl mb-4">⚠️</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Restart Level?</h2>
              <p className="text-gray-600 mb-4">
                Are you sure you want to restart? This will cost <span className="text-red-500 font-bold">1 ❤️</span> life.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={cancelRestart}
                  variant="outline"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmRestart}
                  className="px-6 bg-red-500 hover:bg-red-600 text-white"
                >
                  Restart
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <WinModal 
        isOpen={gameStatus === 'won'} 
        score={score}
        movesLeft={movesLeft}
        playerName={user?.playerName}
        levelId={currentLevelId}
        hasNextLevel={currentLevelId < LEVELS.length}
        onNextLevel={handleNextLevel}
        onBackToMap={handleBackToMap}
        // XP Props
        xpGained={xpAnimationData?.xpGained || 0}
        previousXP={xpAnimationData?.previousXP || 0}
        newXP={xpAnimationData?.newXP || 0}
        previousLevel={xpAnimationData?.previousLevel || 1}
        newLevel={xpAnimationData?.newLevel || 1}
        neededXP={xpAnimationData?.neededXP || 1000}
        // Coins Props
        coinsEarned={xpAnimationData?.coinsEarned || 0}
        starsEarned={xpAnimationData?.starsEarned || 0}
        // Battle Pass Props
        battlePassXP={xpAnimationData?.battlePassXP || 0}
        battlePassBonusXP={xpAnimationData?.battlePassBonusXP || 0}
        isPremium={xpAnimationData?.isPremium || false}
        // Item Drops
        itemDrops={itemDrops}
        // Sword Bonus Props
        swordBonus={swordBonus}
        swordInfo={swordInfo}
        movesUsed={movesUsed}
        swordMultiplier={swordMultiplier}
      />
      
      <LoseModal 
        isOpen={gameStatus === 'lost'} 
        score={score}
        fruitsCollected={fruitsCollected}
        targetCount={levelConfig.targetCount}
        targetEmoji={levelConfig.targetEmoji}
        hp={hp}
        maxHP={maxHP}
        canPlayAgain={canPlay}
        onPlayAgain={handlePlayAgain}
        onBackToMap={handleBackToMap}
      />

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Power-Up Alerts */}
      <DoublePointsAlert isVisible={showDoublePointsAlert} />
      <ShuffleAlert isVisible={showShuffleAlert} />
      
      {/* Advanced Hint Tooltip */}
      <HintTooltip message={hintMessage} type={hintType} />
      
      <InstaEliminationPicker
        isOpen={showInstaEliminationPicker}
        onSelectFruit={useInstaElimination}
        onCancel={cancelInstaElimination}
      />

      {/* Inventory Panel */}
      <InventoryPanel
        isOpen={showInventory}
        onClose={() => setShowInventory(false)}
        inventory={inventory}
        onUseItem={handleUseConsumable}
        disabled={gameStatus !== 'playing' || isAnimating}
      />

      {/* New Fruit Unlocked Modal */}
      <NewFruitUnlockedModal
        isOpen={showNewFruitModal}
        fruit={unlockedFruit}
        onClose={closeNewFruitModal}
      />

      {/* Exit Level Warning Modal */}
      <ExitLevelWarning
        isOpen={showExitWarning}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
        hp={hp}
        maxHP={maxHP}
      />
      
      {/* Boss Victory Modal */}
      {isBossLevel && (
        <BossVictoryModal
          isOpen={showBossVictory}
          rewards={levelConfig?.rewards}
          onContinue={handleBossVictoryContinue}
        />
      )}
      
      {/* VIP Item Use Modal (for board/obstacle selection) */}
      <VIPItemUseModal
        item={vipItemModal}
        onConfirm={handleVIPItemConfirm}
        onCancel={() => setVipItemModal(null)}
        selectMode={vipItemModal?.useType === 'board_select' ? 'board' : vipItemModal?.useType === 'obstacle_select' ? 'obstacle' : null}
        obstacleTypes={vipItemModal?.obstacleTypes || []}
      />
    </div>
  );
}
